import { Component } from "@angular/core";
import { FormControl } from "@angular/forms";
import { SharedService } from "src/app/services/shared.service";
import { ConfirmService } from "src/app/services/confirm.service";
import { InlineJobLoadingStep } from "../inline-job-loading/inline-job-loading.component";

interface DesignElement {
  name: string;
  code: string;
  category:
  | "button"
  | "input"
  | "form-field"
  | "badge"
  | "step-indicator"
  | "pipeline"
  | "toggle"
  | "slider"
  | "dialog"
  | "view-toggle"
  | "tooltip"
  | "modal-header"
  | "filter-pills"
  | "inline-job-loading";
  preview: string;
  componentType?:
  | "text-input"
  | "select-input"
  | "text-area"
  | "date-picker"
  | "country-code-text-input"
  | "search-input-wrapper"
  | "textarea"
  | "nz-select"
  | "filter-pills"
  | "sort-dropdown";
  componentData?: any;
  htmlContent?: string; // For complex HTML structures like step-indicator and pipeline
}

@Component({
  selector: "app-design-elements-gallery",
  templateUrl: "./design-elements-gallery.component.html",
  styleUrls: ["./design-elements-gallery.component.scss"],
  standalone: false,
})
export class DesignElementsGalleryComponent {
  searchTerm: string = "";
  selectedTabFilter: string = "all";
  selectedCategory:
    | "all"
    | "button"
    | "input"
    | "form-field"
    | "badge"
    | "step-indicator"
    | "pipeline"
    | "toggle"
    | "slider"
    | "dialog"
    | "view-toggle"
    | "tooltip"
    | "modal-header"
    | "filter-pills"
    | "inline-job-loading" = "all";

  // FormControls for form field components preview
  textInputControl = new FormControl("");
  selectInputControl = new FormControl(null);
  textAreaControl = new FormControl("");
  datePickerControl = new FormControl(null);
  countryCodeControl = new FormControl("");

  designElements: DesignElement[] = [
    // Buttons - Primary
    {
      name: "Primary Button",
      code: '<button class="btn btn-primary">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Primary Button Small",
      code: '<button class="btn btn-primary btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Primary Button Large",
      code: '<button class="btn btn-primary btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Primary Button Icon",
      code: '<button class="btn btn-primary btn-icon"><app-icon name="plus" size="16"></app-icon></button>',
      category: "button",
      preview: "Icon",
    },
    // Buttons - Primary Light
    {
      name: "Primary Light Button",
      code: '<button class="btn btn-primary-light">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Primary Light Button Small",
      code: '<button class="btn btn-primary-light btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Primary Light Button Large",
      code: '<button class="btn btn-primary-light btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    // Buttons - Secondary
    {
      name: "Secondary Button",
      code: '<button class="btn btn-secondary">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Secondary Button Small",
      code: '<button class="btn btn-secondary btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Secondary Button Large",
      code: '<button class="btn btn-secondary btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    // Buttons - Ghost
    {
      name: "Ghost Button",
      code: '<button class="btn btn-ghost">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Ghost Button Small",
      code: '<button class="btn btn-ghost btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Ghost Button Large",
      code: '<button class="btn btn-ghost btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Ghost Button Disabled",
      code: '<button class="btn btn-ghost" disabled>Button</button>',
      category: "button",
      preview: "Button",
    },
    // Buttons - Danger
    {
      name: "Danger Button",
      code: '<button class="btn btn-danger">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Danger Button Small",
      code: '<button class="btn btn-danger btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Danger Button Large",
      code: '<button class="btn btn-danger btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Danger Button Disabled",
      code: '<button class="btn btn-danger" disabled>Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Light Border Danger Button (Icon)",
      code: `<button
                  class="btn btn-light-border danger"
                  type="button"
                >
                  <app-icon name="x" size="12"></app-icon>
                </button>`,
      category: "button",
      preview: "Icon",
    },
    {
      name: "Primary Border Button (Add more)",
      code: `<button
                  class="btn btn-primary-border"
                  type="button"
                >
                  <app-icon name="plus" size="12"></app-icon>
                  Add more
                </button>`,
      category: "button",
      preview: "Add more",
    },
    // Buttons - Amber
    {
      name: "Amber Button",
      code: '<button class="btn btn-amber">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Amber Button Small",
      code: '<button class="btn btn-amber btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Amber Button Large",
      code: '<button class="btn btn-amber btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Amber Button Disabled",
      code: '<button class="btn btn-amber" disabled>Button</button>',
      category: "button",
      preview: "Button",
    },
    // Buttons - Amber Light
    {
      name: "Amber Light Button",
      code: '<button class="btn btn-amber-light">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Amber Light Button Small",
      code: '<button class="btn btn-amber-light btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Amber Light Button Large",
      code: '<button class="btn btn-amber-light btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    // Buttons - Success
    {
      name: "Success Button",
      code: '<button class="btn btn-success">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Success Button Small",
      code: '<button class="btn btn-success btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Success Button Large",
      code: '<button class="btn btn-success btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    // Buttons - Success Light
    {
      name: "Success Light Button",
      code: '<button class="btn btn-success-light">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Success Light Button Small",
      code: '<button class="btn btn-success-light btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Success Light Button Large",
      code: '<button class="btn btn-success-light btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    // Buttons - Failed Light
    {
      name: "Failed Light Button",
      code: '<button class="btn btn-failed-light">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Failed Light Button Small",
      code: '<button class="btn btn-failed-light btn-sm">Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Failed Light Button Large",
      code: '<button class="btn btn-failed-light btn-lg">Button</button>',
      category: "button",
      preview: "Button",
    },
    // Buttons - Disabled
    {
      name: "Primary Button Disabled",
      code: '<button class="btn btn-primary" disabled>Button</button>',
      category: "button",
      preview: "Button",
    },
    {
      name: "Secondary Button Disabled",
      code: '<button class="btn btn-secondary" disabled>Button</button>',
      category: "button",
      preview: "Button",
    },
    // Inputs - Text
    {
      name: "Text Input",
      code: '<input type="text" class="input-field" placeholder="Enter text..." />',
      category: "input",
      preview: "Enter text...",
    },
    {
      name: "Text Input with Label",
      code: '<label class="label">Label</label><input type="text" class="input-field" placeholder="Enter text..." />',
      category: "input",
      preview: "Enter text...",
    },
    // Inputs - Email
    {
      name: "Email Input",
      code: '<input type="email" class="input-field" placeholder="Enter email..." />',
      category: "input",
      preview: "Enter email...",
    },
    // Inputs - Password
    {
      name: "Password Input",
      code: '<input type="password" class="input-field" placeholder="Enter password..." />',
      category: "input",
      preview: "••••••••",
    },
    // Inputs - Number
    {
      name: "Number Input",
      code: '<input type="number" class="input-field" placeholder="Enter number..." />',
      category: "input",
      preview: "0",
    },
    // Inputs - Search
    {
      name: "Search Input",
      code: '<input type="search" class="input-field" placeholder="Search..." />',
      category: "input",
      preview: "Search...",
    },
    {
      name: "Search Input Wrapper",
      code: `<div class="search-input-wrapper">
  <app-icon
    name="search"
    size="18"
    color="color: var(--interface-dim);"
  ></app-icon>
  <input
    type="text"
    class="search-input"
    placeholder="Search by Brand, Team, or Campaign ID..."
  />
</div>`,
      category: "input",
      preview: "Search by Brand, Team, or Campaign ID...",
      componentType: "search-input-wrapper",
      componentData: {
        placeholder: "Search by Brand, Team, or Campaign ID...",
      },
    },
    // Inputs - Tel
    {
      name: "Phone Input",
      code: '<input type="tel" class="input-field" placeholder="Enter phone..." />',
      category: "input",
      preview: "Enter phone...",
    },
    // Inputs - URL
    {
      name: "URL Input",
      code: '<input type="url" class="input-field" placeholder="Enter URL..." />',
      category: "input",
      preview: "Enter URL...",
    },
    // Inputs - Date
    {
      name: "Date Input",
      code: '<input type="date" class="input-field" />',
      category: "input",
      preview: "Date",
    },
    // Inputs - Disabled
    {
      name: "Input Disabled",
      code: '<input type="text" class="input-field" placeholder="Disabled input" disabled />',
      category: "input",
      preview: "Disabled input",
    },
    // Inputs - Error State
    {
      name: "Input Error State",
      code: '<input type="text" class="input-field error" placeholder="Error state" />',
      category: "input",
      preview: "Error state",
    },
    // Inputs - Textarea
    {
      name: "Textarea",
      code: `<textarea
  class="form-textarea"
  formControlName="form-textarea"
  placeholder="Add any additional style notes or creative direction..."
></textarea>`,
      category: "input",
      preview: "Add any additional style notes or creative direction...",
      componentType: "textarea",
      componentData: {
        placeholder: "Add any additional style notes or creative direction...",
      },
    },
    // Inputs - Select
    {
      name: "Select Input",
      code: `<nz-select
  [nzPlaceHolder]="'Select Option'"
  [nzDropdownClassName]="'common-dropdown-menu'"
  [class]="'common-dark-dropdown w-100'"
  [ngModel]="selectedTabFilter"
  (ngModelChange)="getCampaignsList()"
>
  <nz-option nzValue="all" nzLabel="All"></nz-option>
  <nz-option nzValue="active" nzLabel="Active"></nz-option>
  <nz-option nzValue="completed" nzLabel="Completed"></nz-option>
</nz-select>`,
      category: "input",
      preview: "Select Option",
      componentType: "nz-select",
      componentData: {
        placeholder: "Select Option",
        options: [
          { value: "all", label: "All" },
          { value: "active", label: "Active" },
          { value: "completed", label: "Completed" },
        ],
      },
    },
    // Form Field Components - Text Input
    {
      name: "Text Input Component",
      code: `<app-text-input
  label="field name"
  placeholder="Enter text..."
  [control]="formControl"
  [isFormSubmitted]="false"
  class="form-input-v1"
></app-text-input>`,
      category: "form-field",
      preview: "Enter text...",
      componentType: "text-input",
      componentData: {
        placeholder: "Enter text...",
        label: "field name",
        class: "form-input-v1",
      },
    },
    {
      name: "Text Input Component (Email)",
      code: `<app-text-input
  label="email"
  placeholder="Enter email..."
  type="email"
  [control]="formControl"
  [isFormSubmitted]="false"
  class="form-input-v1"
></app-text-input>`,
      category: "form-field",
      preview: "Enter email...",
      componentType: "text-input",
      componentData: {
        placeholder: "Enter email...",
        label: "email",
        type: "email",
        class: "form-input-v1",
      },
    },
    {
      name: "Text Input Component (Number)",
      code: `<app-text-input
  label="count"
  placeholder="Enter number..."
  type="number"
  [control]="formControl"
  [isFormSubmitted]="false"
  min="1"
  class="form-input-v1"
></app-text-input>`,
      category: "form-field",
      preview: "0",
      componentType: "text-input",
      componentData: {
        placeholder: "Enter number...",
        label: "count",
        type: "number",
        min: "1",
        class: "form-input-v1",
      },
    },
    {
      name: "Text Input Component (Password)",
      code: `<app-text-input
  label="password"
  placeholder="Enter password..."
  type="password"
  [control]="formControl"
  [isFormSubmitted]="false"
  class="form-input-v1"
></app-text-input>`,
      category: "form-field",
      preview: "••••••••",
      componentType: "text-input",
      componentData: {
        placeholder: "Enter password...",
        label: "password",
        type: "password",
        class: "form-input-v1",
      },
    },
    // Form Field Components - Select Input
    {
      name: "Select Input Component",
      code: `<app-select-input
  label="option"
  placeholder="Select option"
  [control]="formControl"
  [isFormSubmitted]="false"
  [items]="options"
  valueKey="id"
  labelKey="name"
  [allowClear]="false"
  class="common-dark-dropdown w-100"
  [dropdownClassName]="'common-dropdown-menu'"
></app-select-input>`,
      category: "form-field",
      preview: "Select option",
      componentType: "select-input",
      componentData: {
        placeholder: "Select option",
        label: "option",
        allowClear: false,
      },
    },
    {
      name: "Select Input Component (Searchable)",
      code: `<app-select-input
  label="option"
  placeholder="Select option"
  [control]="formControl"
  [isFormSubmitted]="false"
  [items]="options"
  valueKey="id"
  labelKey="name"
  [showSearch]="true"
  [allowClear]="true"
  class="common-dark-dropdown w-100"
  [dropdownClassName]="'common-dropdown-menu'"
></app-select-input>`,
      category: "form-field",
      preview: "Select option",
      componentType: "select-input",
      componentData: {
        placeholder: "Select option",
        label: "option",
        showSearch: true,
        allowClear: true,
      },
    },
    // Form Field Components - Text Area
    {
      name: "Text Area Component",
      code: `<app-text-area
  label="description"
  placeholder="Enter description..."
  [control]="formControl"
  [isFormSubmitted]="false"
  [rowsCount]="4"
  class="form-input-v1"
></app-text-area>`,
      category: "form-field",
      preview: "Enter description...",
      componentType: "text-area",
      componentData: {
        placeholder: "Enter description...",
        label: "description",
        rowsCount: 4,
        class: "form-input-v1",
      },
    },
    // Form Field Components - Date Picker
    {
      name: "Date Picker Component",
      code: `<app-date-picker
  label="date"
  [control]="formControl"
  [isFormSubmitted]="false"
  class="form-time-picker-input"
></app-date-picker>`,
      category: "form-field",
      preview: "Select date",
      componentType: "date-picker",
      componentData: { label: "date", class: "form-time-picker-input" },
    },
    // Form Field Components - Country Code Text Input
    {
      name: "Country Code Text Input Component",
      code: `<app-country-code-text-input
  label="phone number"
  placeholder="Enter phone number"
  [control]="formControl"
  [isFormSubmitted]="false"
  countryCode="+91"
  class="form-input-v1"
></app-country-code-text-input>`,
      category: "form-field",
      preview: "Enter phone number",
      componentType: "country-code-text-input",
      componentData: {
        placeholder: "Enter phone number",
        label: "phone number",
        countryCode: "+91",
        class: "form-input-v1",
      },
    },
    // Form Field - Sort Dropdown
    {
      name: "Sort Dropdown",
      code: `<div
  class="sort-dropdown"
  nz-dropdown
  nzTrigger="click"
  [nzDropdownMenu]="menu"
  [nzOverlayClassName]="'sort-dropdown-menu'"
>
  <span class="sort-label">Sort:</span>
  <span class="sort-value">{{ selectedSort?.label }}</span>
  <nz-dropdown-menu #menu="nzDropdownMenu">
    <ul nz-menu>
      @for (item of sortOptions; track item.value) {
      <li [class.active]="selectedSort?.value == item?.value" nz-menu-item (click)="selectedSort = item">{{ item?.label }}</li>
      }
    </ul>
  </nz-dropdown-menu>
</div>`,
      category: "form-field",
      preview: "Sort: Most Recent",
      componentType: "sort-dropdown",
      componentData: {
        options: [
          { value: "most_recent", label: "Most Recent" },
          { value: "oldest", label: "Oldest" },
          { value: "most_videos", label: "Most Videos" },
        ],
      },
    },
    // Badges
    {
      name: "Badge Cyan",
      code: '<span class="badge badge-cyan">CYAN</span>',
      category: "badge",
      preview: "CYAN",
    },
    {
      name: "Badge Amber",
      code: '<span class="badge badge-amber">AMBER</span>',
      category: "badge",
      preview: "AMBER",
    },
    {
      name: "Badge Success",
      code: '<span class="badge badge-success">SUCCESS</span>',
      category: "badge",
      preview: "SUCCESS",
    },
    {
      name: "Badge Error",
      code: '<span class="badge badge-error">ERROR</span>',
      category: "badge",
      preview: "ERROR",
    },
    {
      name: "Badge Neutral",
      code: '<span class="badge badge-neutral">NEUTRAL</span>',
      category: "badge",
      preview: "NEUTRAL",
    },
    // Filter Pills
    {
      name: "Filter Pills",
      code: `<div class="filter-pills">
  <button
    class="filter-pill"
    [class.active]="selectedTabFilter === 'all'"
    (click)="setFilter('all')"
  >
    All <span class="pill-count">{{ filterCounts.all }}</span>
  </button>
  <button
    class="filter-pill"
    [class.active]="selectedTabFilter === 'active'"
    (click)="setFilter('active')"
  >
    Active <span class="pill-count">{{ filterCounts.active }}</span>
  </button>
</div>`,
      category: "filter-pills",
      preview: "Filter Pills",
      componentType: "filter-pills",
      htmlContent: `<div class="filter-pills">
  <button class="filter-pill active">All <span class="pill-count">7</span></button>
  <button class="filter-pill">Active <span class="pill-count">3</span></button>
</div>`,
    },
    // Step Indicators
    {
      name: "Step Indicator - 3 Steps (Second Active)",
      code: `<div class="step-indicator">
  <div class="step-item completed">
    <div class="step-number">1</div>
    <span class="step-label">Step 1</span>
  </div>
  <div class="step-connector completed"></div>
  <div class="step-item active">
    <div class="step-number">2</div>
    <span class="step-label">Step 2</span>
  </div>
  <div class="step-connector"></div>
  <div class="step-item">
    <div class="step-number">3</div>
    <span class="step-label">Step 3</span>
  </div>
</div>`,
      category: "step-indicator",
      preview: "Step Indicator",
      htmlContent: `<div class="step-indicator">
  <div class="step-item completed">
    <div class="step-number">1</div>
    <span class="step-label">Step 1</span>
  </div>
  <div class="step-connector completed"></div>
  <div class="step-item active">
    <div class="step-number">2</div>
    <span class="step-label">Step 2</span>
  </div>
  <div class="step-connector"></div>
  <div class="step-item">
    <div class="step-number">3</div>
    <span class="step-label">Step 3</span>
  </div>
</div>`,
    },
    // Pipeline

    {
      name: "Pipeline",
      code: `<div class="pipeline">
  <div class="pipeline-track"></div>
  <div class="pipeline-progress"></div>
  <div class="pipeline-node completed">
    <div class="node-indicator">01</div>
    <div class="node-label">Profile</div>
  </div>
  <div class="pipeline-node active">
    <div class="node-indicator">02</div>
    <div class="node-label">Studio</div>
  </div>
  <div class="pipeline-node">
    <div class="node-indicator">03</div>
    <div class="node-label">Plan</div>
  </div>
</div>`,
      category: "pipeline",
      preview: "Pipeline",
      htmlContent: `<div class="pipeline">
  <div class="pipeline-track"></div>
  <div class="pipeline-progress"></div>
  <div class="pipeline-node completed">
    <div class="node-indicator">01</div>
    <div class="node-label">Profile</div>
  </div>
  <div class="pipeline-node active">
    <div class="node-indicator">02</div>
    <div class="node-label">Studio</div>
  </div>
  <div class="pipeline-node">
    <div class="node-indicator">03</div>
    <div class="node-label">Plan</div>
  </div>
</div>`,
    },
    // Toggles
    {
      name: "Toggle Switch",
      code: `<label class="toggle-container">
  <input type="checkbox" class="toggle-checkbox" id="toggle1">
  <span class="toggle-switch"></span>
</label>`,
      category: "toggle",
      preview: "Toggle Switch",
      htmlContent: `<label class="toggle-container">
  <input type="checkbox" class="toggle-checkbox" id="toggle1">
  <span class="toggle-switch"></span>
</label>`,
    },
    {
      name: "Compact Checkbox",
      code: `<label style="display:inline-flex;align-items:center;gap:8px;color:var(--interface-silver);font-size:12px;">
  <input type="checkbox" class="checkbox-compact" checked />
  <span>Checkbox</span>
</label>`,
      category: "toggle",
      preview: "Compact Checkbox",
      htmlContent: `<label style="display:inline-flex;align-items:center;gap:8px;color:var(--interface-silver);font-size:12px;">
  <input type="checkbox" class="checkbox-compact" checked />
  <span>Checkbox</span>
</label>`,
    },
    // Sliders
    {
      name: "Slider Group",
      code: `<div class="slider-group">
  <div class="slider-header">
    <span class="slider-label-left">Formal</span>
    <span class="slider-label-right">Casual</span>
  </div>
  <div class="slider-track">
    <div class="slider-fill" style="width: 70%"></div>
    <input
      type="range"
      class="slider-input"
      min="1"
      max="5"
      value="4"
    />
  </div>
</div>`,
      category: "slider",
      preview: "Slider Group",
      htmlContent: `<div class="slider-group">
  <div class="slider-header">
    <span class="slider-label-left">Formal</span>
    <span class="slider-label-right">Casual</span>
  </div>
  <div class="slider-track">
    <div class="slider-fill" style="width: 70%"></div>
    <input
      type="range"
      class="slider-input"
      min="1"
      max="5"
      value="4"
    />
  </div>
</div>`,
    },
    // Dialogs
    {
      name: "Exit Organization Confirmation Dialog",
      code: `this.confirmService
    .confirm(
      "Are you sure you want to exit this organization? You will be logged out for the changes to take effect.",
      "",
    )
    .subscribe((result) => {
      if (result) {
        
      }
    });`,
      category: "dialog",
      preview: "Exit Organization Confirmation",
    },
    // Modal Header
    {
      name: "Modal Header (Campaign Soul)",
      code: `<ng-template #campaignTitleTemplate>
  <div class="modal-header-left">
    <div class="modal-header-icon">
      <app-icon name="star" size="16"></app-icon>
    </div>
    <div class="modal-header-text">
      <h2 class="modal-header-title">Setup Campaign Soul</h2>
      <span class="modal-header-sub">
        Define the strategic DNA of your campaign
      </span>
    </div>
  </div>
</ng-template>`,
      category: "modal-header",
      preview: "Modal Header",
    },
    // Tooltips - Info Icon with dAIrector design
    {
      name: "Info Icon with Tooltip",
      code: `<span
  class="info-icon"
  nz-tooltip="Your tooltip text here"
>
  <app-icon name="info-i" size="20"></app-icon>
</span>`,
      category: "tooltip",
      preview: "Info Icon",
      htmlContent: `<span class="info-icon" nz-tooltip="Hover to see tooltip — dark bg, cyan border">
  <app-icon name="info-i" size="20"></app-icon>
</span>`,
    },
    {
      name: "Metric Label with Info Icon",
      code: `<div class="metric-label">
  Total Campaigns
  <span class="info-icon" nz-tooltip="Number of active campaign umbrellas">
    <app-icon name="info-i" size="20"></app-icon>
  </span>
</div>`,
      category: "tooltip",
      preview: "Metric Label + Icon",
      htmlContent: `<div class="metric-label">
  Total Campaigns
  <span class="info-icon" nz-tooltip="Number of active campaign umbrellas">
    <app-icon name="info-i" size="20"></app-icon>
  </span>
</div>`,
    },
    // View Toggle
    {
      name: "View Toggle (Shotlist/Storyboard)",
      code: `<div class="view-toggle">
  <button class="view-toggle-btn">Shotlist</button>
  <button class="view-toggle-btn active">Storyboard</button>
</div>`,
      category: "view-toggle",
      preview: "View Toggle",
      htmlContent: `<div class="view-toggle">
  <button class="view-toggle-btn">Shotlist</button>
  <button class="view-toggle-btn active">Storyboard</button>
</div>`,
    },
    // Inline job loading (shared overlay)
    {
      name: "Inline Job Loading (steps + progress)",
      code: `<app-inline-job-loading
  iconName="file-text-docx"
  [iconSize]="28"
  title="Your title"
  subtitle="Your subtitle"
  progressMode="steps"
  [progressPercent]="scriptGenProgressPercent"
  [steps]="scriptGenLoadingSteps"
/>`,
      category: "inline-job-loading",
      preview: "Steps",
    },
    {
      name: "Inline Job Loading (indeterminate)",
      code: `<app-inline-job-loading
  iconName="file-text-docx"
  [iconSize]="28"
  title="Your title"
  subtitle="Your subtitle"
  progressMode="indeterminate"
/>`,
      category: "inline-job-loading",
      preview: "Indeterminate",
    },
  ];

  /** Demo data for design gallery preview (steps mode). */
  inlineJobDemoSteps: InlineJobLoadingStep[] = [
    {
      label: "Generation request submitted",
      done: true,
      active: false,
    },
    {
      label: "Running script generation job",
      done: true,
      active: false,
    },
    {
      label: "Processing screenplay output",
      done: false,
      active: true,
    },
    {
      label: "Finalizing script",
      done: false,
      active: false,
    },
  ];

  inlineJobDemoProgress = 52;

  // Sample options for select input preview
  selectOptions = [
    { id: 1, name: "Option 1" },
    { id: 2, name: "Option 2" },
    { id: 3, name: "Option 3" },
  ];

  // Sort dropdown preview
  sortOptions: { value: string; label: string }[] = [
    { value: "most_recent", label: "Most Recent" },
    { value: "oldest", label: "Oldest" },
    { value: "most_videos", label: "Most Videos" },
  ];
  selectedSort: { value: string; label: string } = this.sortOptions[0];

  constructor(
    private sharedService: SharedService,
    private confirmService: ConfirmService,
  ) { }

  get filteredElements(): DesignElement[] {
    let filtered = this.designElements;

    // Filter by category
    if (this.selectedCategory !== "all") {
      filtered = filtered.filter(
        (element) => element.category === this.selectedCategory,
      );
    }

    // Filter by search term
    const term = this.searchTerm.toLowerCase().trim();
    if (term) {
      filtered = filtered.filter((element) =>
        element.name.toLowerCase().includes(term),
      );
    }

    return filtered;
  }

  copyElement(element: DesignElement): void {
    navigator.clipboard.writeText(element.code).then(
      () => {
        this.sharedService.messageToastNotification(
          "success",
          "Copied to clipboard!",
        );
      },
      (err) => {
        console.error("Could not copy text: ", err);
        this.sharedService.messageToastNotification(
          "error",
          "Failed to copy to clipboard",
        );
      },
    );
  }

  setCategory(
    category:
      | "all"
      | "button"
      | "input"
      | "form-field"
      | "badge"
      | "step-indicator"
      | "pipeline"
      | "toggle"
      | "slider"
      | "dialog"
      | "view-toggle"
      | "tooltip"
      | "modal-header"
      | "filter-pills"
      | "inline-job-loading",
  ): void {
    this.selectedCategory = category;
  }

  getFormControlForElement(element: DesignElement): FormControl {
    switch (element.componentType) {
      case "text-input":
        return this.textInputControl;
      case "select-input":
        return this.selectInputControl;
      case "text-area":
        return this.textAreaControl;
      case "date-picker":
        return this.datePickerControl;
      case "country-code-text-input":
        return this.countryCodeControl;
      case "textarea":
        return this.textAreaControl;
      default:
        return new FormControl("");
    }
  }

  getButtonClasses(code: string): string {
    const classMatch = code.match(/class="([^"]+)"/);
    if (classMatch) {
      return classMatch[1];
    }
    return "btn btn-primary";
  }

  getInputClasses(code: string): string {
    const classMatch = code.match(/class="([^"]+)"/);
    if (classMatch) {
      return classMatch[1];
    }
    return "input-field";
  }

  getInputType(code: string): string {
    const typeMatch = code.match(/type="([^"]+)"/);
    if (typeMatch) {
      return typeMatch[1];
    }
    return "text";
  }

  getBadgeClasses(code: string): string {
    const classMatch = code.match(/class="([^"]+)"/);
    if (classMatch) {
      return classMatch[1];
    }
    return "badge badge-cyan";
  }

  getCampaignsList(): void {
    // Dummy method for now
  }
}
